import { useState } from "react"
import type { ApolloError } from "@apollo/client"
import Separator from "@/v2-ui/separator"
import cn from "@/v2-ui/utils/utils.cn"
import Badge from "@/v2-backoffice-client/components/badge"
import Table, {
  createColumns,
  type TableProps,
} from "@/v2-backoffice-client/components/table"
import CustomerOwnershipBadge from "@/v2-backoffice-client/customer/ownership/CustomerOwnershipBadge"
import { getCrossCustomerMembership } from "@/v2-backoffice-client/customer/ownership/customer.ownership.utils"
import type { UserTenantFragment } from "@/v2-backoffice-client/user/tenant/__types__/user.tenant.fragment"
import UserTenantManageButton from "@/v2-backoffice-client/user/tenant/manage/UserTenantManageButton"
import TenantUserRemoveManyButton from "@/v2-backoffice-client/tenant/user/remove/many/TenantUserRemoveManyButton"
import TenantUserRestoreManyButton from "@/v2-backoffice-client/tenant/user/restore/many/TenantUserRestoreManyButton"
import UserTenantTableRowToolbar from "@/v2-backoffice-client/user/tenant/UserTenantTableRowToolbar"

const ACTIVE_STATUS_CODE = 0
const DELETED_STATUS_CODE = 1

const columns = createColumns<UserTenantFragment>({
  enableSelection: true,
  enableSorting: true,
  enableHiding: true,
  columns: {
    "Tenant.Name": {
      id: "Tenant.Name",
      label: "Name",
      to: ({ row }) => `>>?v=tenant:${row.original.TenantId}`,
      renderCell({ row }) {
        return row.original.Tenant?.Name ?? null
      },
    },
    "UserTenantType.Name": {
      id: "UserTenantType.Name",
      label: "Role",
      enableGrouping: true,
      filter: {
        type: "select",
        placeholder: "Filter by role...",
        options: [
          { label: "Admin", value: "1" },
          { label: "User", value: "2" },
          { label: "Viewer", value: "3" },
        ],
      },
    },
    // The user's active team (Configuration.ActiveTenantId) — row-local
    // comparison against the nested User fragment, so the column works
    // wherever the table renders.
    IsActive: {
      id: "IsActive",
      label: "Active",
      defaultSize: 90,
      getValue: (row) =>
        row.User?.ActiveTenantId != null &&
        row.User.ActiveTenantId === row.TenantId,
      renderCell({ row }) {
        const isActive =
          row.original.User?.ActiveTenantId != null &&
          row.original.User.ActiveTenantId === row.original.TenantId
        if (!isActive) return null
        return (
          <Badge
            variant="positive"
            iconSrc="check"
            tooltip="The team the user is currently active in (Configuration.ActiveTenantId)."
          >
            Active
          </Badge>
        )
      },
    },
    CustomerName: {
      id: "CustomerName",
      label: "Customer",
      enableGrouping: true,
      getValue: (row) => row.Tenant?.CustomerName,
      renderCell({ row }) {
        const name = row.original.Tenant?.CustomerName
        if (!name) return null
        // Flag a team that belongs to another customer than the one that
        // owns the user: rare on purpose, usually a wrong row picked.
        const crossing = getCrossCustomerMembership(
          row.original.User,
          row.original.Tenant,
        )
        if (!crossing) return name
        return (
          <span className="inline-flex items-center gap-2">
            {name}
            <CustomerOwnershipBadge crossing={crossing} perspective="user" />
          </span>
        )
      },
      to: ({ row }) => {
        const externalId = row.original.Tenant?.ExternalCustomerId
        if (!externalId) return
        return `>>?v=customer:${externalId}`
      },
    },
    Id: {
      id: "Id",
      label: "User Team Id",
      defaultSize: 110,
      defaultHidden: true,
    },
  },
})

export type UserTenantTableProps = Omit<
  TableProps<UserTenantFragment>,
  "data" | "columns"
> & {
  userId?: number
  userName?: string
  isLoading?: boolean
  error?: ApolloError | Error
  userTenants: UserTenantFragment[]
  activeCount?: number
  removedCount?: number
  onDeletedFilterChange?(onlyDeleted: boolean | undefined): void
  onRefresh?(): void
}

function UserTenantTable(props: UserTenantTableProps) {
  const {
    className,
    userId,
    userName,
    userTenants,
    activeCount,
    removedCount,
    onDeletedFilterChange,
    onRefresh,
    isLoading,
    error,
    ...tableProps
  } = props

  const [onlyDeleted, setOnlyDeleted] = useState(false)

  function handleStatusChange(value: number | null) {
    const deleted = value === DELETED_STATUS_CODE
    setOnlyDeleted(deleted)
    onDeletedFilterChange?.(value === null ? undefined : deleted)
  }

  return (
    <Table
      className={cn("user-tenant-table", className)}
      tableId="user-tenant-table"
      data={userTenants}
      columns={columns}
      enableGrouping
      enableRowSelection
      {...tableProps}
    >
      <Table.Toolbar>
        <Table.StatusFilter
          defaultValue={ACTIVE_STATUS_CODE}
          onChange={handleStatusChange}
        >
          <Table.StatusFilter.Option
            value={null}
            count={
              activeCount !== undefined && removedCount !== undefined
                ? activeCount + removedCount
                : undefined
            }
          >
            All
          </Table.StatusFilter.Option>

          <Table.StatusFilter.Option
            variant="success"
            value={ACTIVE_STATUS_CODE}
            count={activeCount}
          >
            Active
          </Table.StatusFilter.Option>

          <Table.StatusFilter.Option
            variant="destructive"
            value={DELETED_STATUS_CODE}
            count={removedCount}
          >
            Removed
          </Table.StatusFilter.Option>
        </Table.StatusFilter>

        <div className="flex-1" />

        <Table.Search column="Tenant.Name" placeholder="Search by name..." />
        <Separator orientation="vertical" />

        <Table.ToolbarMenu onRefresh={onRefresh} exportFileName="user-tenants">
          {userId && !onlyDeleted && (
            <UserTenantManageButton
              userId={userId}
              userName={userName}
              existingUserTenants={userTenants}
            />
          )}
        </Table.ToolbarMenu>
      </Table.Toolbar>

      <Table.FilterList />
      <Table.GroupList />

      <Table.RowToolbar<UserTenantFragment> column="Tenant.Name">
        {(userTenant) => (
          <UserTenantTableRowToolbar
            userId={userId ?? 0}
            userTenant={userTenant}
            onlyDeleted={onlyDeleted}
          />
        )}
      </Table.RowToolbar>

      <Table.Content isLoading={isLoading} error={error} />

      <Table.SelectionToolbar<UserTenantFragment>>
        {(uts, reset) => {
          const ids = uts.map((ut) => ut.Id)
          return (
            <>
              <Table.ExportSelectionButton
                fileName="user-tenants"
                selectionCount={uts.length}
              />
              {onlyDeleted ? (
                <TenantUserRestoreManyButton
                  isStacked
                  userTenantIds={ids}
                  onCompleted={reset}
                >
                  Restore
                </TenantUserRestoreManyButton>
              ) : (
                <TenantUserRemoveManyButton
                  isStacked
                  userTenantIds={ids}
                  onCompleted={reset}
                >
                  Remove
                </TenantUserRemoveManyButton>
              )}
            </>
          )
        }}
      </Table.SelectionToolbar>
    </Table>
  )
}

export default UserTenantTable
